import { OrderListItem } from 'app/src/features/UserAccount/components/OrderListItem'

import type { GenericOrderItem, OrderListFilters } from 'app/src/model'
import {
  shoppingApi,
  type OrderListSortKind,
  type OrderListStatusFilter,
  type StatusConfigType,
} from 'app/src/services/welfareportal'
import { ListItemRenderer } from 'app/src/shared/components/ListItemRenderer'
import { getValueFromTypology } from 'app/src/shared/helpers'
import { NavigatePaths, useTranslation, useWebAppContext } from 'app/src/shared/hooks'
import { ProgressSpinner } from 'primereact/progressspinner'
import { useEffect, useState, type Dispatch, type ReactElement, type SetStateAction } from 'react'
import { useLocation } from 'react-router-dom'

export function UserAccountDiscountOrderListSection(props: {
  discountStatusList: StatusConfigType[]
  filtersToSend: OrderListFilters
  updateFilters: Dispatch<SetStateAction<OrderListFilters>>
}): ReactElement {
  const { t } = useTranslation('userAccount', { keyPrefix: 'Discount' })
  const context = useWebAppContext()
  const globalT = useTranslation('home')
  const { pathname } = useLocation()

  const discountListResponse = shoppingApi.useOrderList(
    {
      queryParams: {
        additionalInfoPrize: false,
        sort: props.filtersToSend.sort as OrderListSortKind,
        ...(props.filtersToSend.status !== undefined && { status: props.filtersToSend.status as OrderListStatusFilter }),
        ...(props.filtersToSend.from !== undefined && { from: props.filtersToSend.from }),
        ...(props.filtersToSend.to !== undefined && { to: props.filtersToSend.to }),
        ...(props.filtersToSend.offset !== undefined && { pagingOffset: props.filtersToSend.offset }),
        ...(props.filtersToSend.limit !== undefined && { pagingLimit: props.filtersToSend.limit }),
        membershipId: props.filtersToSend.membershipId,
        typology: 'discount',
      },
    },
    {
      enabled:
        !!props.filtersToSend?.membershipId &&
        pathname === NavigatePaths.UserAccount &&
        props.filtersToSend?.status &&
        props.filtersToSend.status.length > 0,
    },
  )

  const [itemsToDisplay, setItemsToDisplay] = useState<shoppingApi.OrderListItem[]>()

  useEffect(() => {
    if (
      discountListResponse &&
      !discountListResponse.isError &&
      discountListResponse.data?.paging &&
      discountListResponse.data.paging.offset > 0
    ) {
      setItemsToDisplay([...(itemsToDisplay ?? []), ...(discountListResponse.data?.items ?? [])])
    } else if (discountListResponse.data?.items && discountListResponse.data?.paging?.offset === 0) {
      setItemsToDisplay(discountListResponse.data.items)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountListResponse.data])

  return (
    <>
      {itemsToDisplay && itemsToDisplay.length > 0 ? (
        <ListItemRenderer
          pt={{
            container: {
              className: 'grid gap-4',
            },
            paginator: {
              renderedInitialPageSize: context.portalConfiguration.paginatorConfig.renderedInitialPageSize ?? 1000,
              initialPageSize: context.portalConfiguration.paginatorConfig.initialPageSize ?? 1000,
            },
          }}
          data={{ paging: discountListResponse?.data?.paging, items: itemsToDisplay }}
          loadMore={(limit: number, offset: number) => {
            props.updateFilters({
              ...props.filtersToSend,
              limit: limit,
              offset: offset,
            })
          }}
          loading={discountListResponse?.isLoading}
          renderItem={(item, i) => {
            const genericItem: GenericOrderItem = {
              orderDate: item.orderDate,
              productTypology: item.productTypology,
              price: 0,
              discountValue: getValueFromTypology(item.priceDetails?.discountType, item.priceDetails?.discountValue),
              discountType: item.priceDetails?.discountType,
              priceLabel: t('PriceLabel'),
              productName: item.productName,
              status: props.discountStatusList.find((i) => i.key == item.status)?.label ?? 'N/A',
              seeDetailsLabel: t('SeeDetailsLabel'),
              detailsLink: item.detailPageUrl,
            }
            return <OrderListItem key={i} item={genericItem}></OrderListItem>
          }}
        />
      ) : !discountListResponse.isLoading ? (
        <div className='mt-10 grid text-center font-bold'>{globalT.t('Global.Paginator.EmptyMessage')}</div>
      ) : (
        <div className='card justify-content-center flex'>
          <ProgressSpinner data-testid='loading-element' aria-label={globalT.t('Global.Loading.Message')} />
        </div>
      )}
    </>
  )
}
